import { useState, useEffect } from "react";
import type { CascaderProps } from "antd";

interface CascaderOption {
  label: string;
  value: number | string;
  children?: CascaderOption[];
  isLeaf?: boolean;
  loading?: boolean;
}

interface UseCascaderProps {
  fetchData: (id: number | string) => Promise<any>;
  initId?: number[] | string[]; // 初始加载ID
}

export const useCascader = ({
  fetchData,
  initId = [100000],
}: UseCascaderProps) => {
  const [options, setOptions] = useState<CascaderOption[]>([]);

  // 初始化数据
  useEffect(() => {
    // console.log(options, 'optionsoptionsoptions');
    initFetchData();
  }, []);

  const initFetchData = async () => {
    if (initId.length === 0) return;

    try {
      const { data } = await fetchData(initId[0]);
      setOptions(
        data.map((item: any) => ({
          label: item.name,
          value: item.id,
          isLeaf: false,
        }))
      );
    } catch (error) {
      console.error("初始化数据失败:", error);
    }
  };
  

  // 加载数据的处理函数
  const loadData = async (selectedOptions: CascaderOption[]) => {
    const targetOption = selectedOptions[selectedOptions.length - 1];
    targetOption.loading = true;

    try {
      const { data } = await fetchData(targetOption.value);

      targetOption.children = data.map((item: any) => ({
        label: item.name,
        value: item.id,
        isLeaf: selectedOptions.length < 2 ? false : true,
      }));

      setOptions([...options]);
    } catch (error) {
      console.error("加载数据失败:", error);
    } finally {
      targetOption.loading = false;
    }
  };

  // 新增: 根据 ID 数组预加载数据
  const loadByIds = async (ids: (string | number)[]) => {
    if (!ids || ids.length < 1) return;
    
    // 过滤掉空值
    const validIds = ids.filter(id => id !== "" && id !== null && id !== undefined);
    if (validIds.length === 0) return;

    try {
      // 确保省级数据已加载
      if (options.length === 0) {
        const { data } = await fetchData(100000);
        setOptions(data.map((item: any) => ({
          label: item.name,
          value: item.id,
          isLeaf: false,
        })));
      }
      
      // 找到省级选项
      const provinceId = validIds[0];
      const provinceOption = options.find(opt => String(opt.value) === String(provinceId));
      
      if (!provinceOption) {
        console.error(`找不到省级选项: ${provinceId}`);
        return;
      }
      
      // 加载省级下的市级数据
      const { data: cityData } = await fetchData(provinceId);
      
      // 更新省级选项的子节点
      provinceOption.children = cityData.map((item: any) => ({
        label: item.name,
        value: item.id,
        isLeaf: true, // 市级是最后一级，设为叶子节点
      }));
      
      // 加载市级下的区/县数据
      const cityId = validIds[1];
      const cityOption = provinceOption.children?.find(opt => String(opt.value) === String(cityId));
     
      if (!cityOption) {
        console.error(`找不到市级选项: ${cityId}`);
        return;
      }
      
      // 加载市级下的区/县数据
      const { data: districtData } = await fetchData(cityId);
      
      // 更新市级选项的子节点
      cityOption.children = districtData.map((item: any) => ({
        label: item.name,
        value: item.id,
        isLeaf: false, // 区/县是最后一级，设为叶子节点
      }));

      // 更新状态
      setOptions([...options]);

    } catch (error) {
      console.error("根据ID加载数据失败:", error);
    }
  };

  const cascaderProps = {
    options,
    loadData,
    changeOnSelect: true,
    // fieldNames: {
    //   label: 'name',
    //   value: 'id',
    //   children: 'children',
    // },
  };

  return {
    options,
    setOptions,
    loadData,
    loadByIds,
    cascaderProps,
  };
};
